import {PageProps} from "gatsby";

export interface Application {
  data: AppData;
  location: PageProps["location"];
}

export interface AppData {
  professional: ProfessionalResponse;
}

export interface ProfessionalResponse {
  data: Professional;
}

export interface Professional {
  id: string;
  shortName: string;
  longName: string;
  description: string;
  language: string;
  languages: string[];
  mobility: { unit: string; radius: number } | null;
  businessInfo: IBusinessInfo;
  location: ILocation & { placeId: string };
  locations: (ILocation & { placeId: string })[];
  picture?: IPicture;
  additionalPictures: IPicture[];
  videos: string[];
  instagram: string;
  openingTime: IOpeningTime;
  education: IEducation[];
  socialNetworks: ISocialNetwork[];
  externalLinks: IExternalLink[];
  template: ITemplate;
  profAssociations: string[];
  awards: IAwards[];
  testimonials: string[];
  subdomain: string;
  googleAnalyticCode: string;
  services: IService[];
  sectors: ISector[];
  customSectors: string[];
  team: ITeamMember[];
}

export interface ITeamMember {
  name?: string;
  contact?: string;
  position?: string;
  picture?: IPicture;
}

export interface IBusinessInfo {
  email: string;
  phone: string;
  euVatNumber: string;
  officialId?: string;
  paymentMethod: PaymentMethod[];
}

export enum PaymentMethod {
  Cash = 'Cash',
  Check = 'Check',
  CreditCard = 'Credit card',
  DebitCard = 'Debit card',
  MobilePayment = 'Mobile payment',
  BankTransfer = 'Bank transfer',
}

export interface ILocation {
  placeId: string;
  name: string;
  longitude: number;
  latitude: number;
  placeDescription: string;
}

export interface IPicture {
  id: string;
  url: string;
  group?: string;
  description?: string;
}

export interface IOpeningTime {
  monday: IWorkingDayInfo;
  tuesday: IWorkingDayInfo;
  wednesday: IWorkingDayInfo;
  thursday: IWorkingDayInfo;
  friday: IWorkingDayInfo;
  saturday: IWorkingDayInfo;
  sunday: IWorkingDayInfo;
}

export interface IWorkingDayInfo {
  open: string;
  closed: string;
  availability: Availability;
}

export type Availability = 'open' | 'closed' | 'byAppointment';

export interface IEducation {
  certificateName: string;
  deliveredBy: string;
  yearObtained: number;
}

export interface IAwards {
  description: string;
  year: number;
}

export interface ITemplate {
  template: string;
  color: string;
}

export interface ISocialNetwork {
  name: SocialNetworkType;
  url: string;
}
export interface IExternalLink {
  linkDescription: string;
  url: string;
}

export interface ITranslated {
  en: string;
  fr: string;
}

export interface IBenefit {
  title: string;
  translated?: ITranslated;
}

export enum SocialNetworkType {
  Facebook = 'Facebook',
  Twitter = 'Twitter',
  LinkedIn = 'LinkedIn',
  Instagram = 'Instagram',
  Youtube = 'Youtube',
}

export interface IService {
  id: string;
  isPrimary: boolean;
  sector: ISector;
  customSector: string;
  specialties: ISpecialty[];
  customSpecialties: string[];
  customHelps: string[];
  customAppointmentTypes: string[];
  appointmentTypes: IAppointmentTypes[];
  experience: IExperience;
  helps: IBenefit[];
  targetAudience: TargetAudience[];
  rates: IRates[];
  ratesDescription?: string;
  picture?: IPicture;
}

export interface ISector {
  id: string;
  title: string;
  activityType: ActivityType;
  pictures: string[];
  benefits?: IBenefit[];
  malePractitioner: string;
  femalePractitioner: string;
  descriptionTitle: string;
  descriptionSubTitle: string;
  shortDescription: string;
  longDescription: string;
  specialties?: ISpecialty[];
  translated?: ITranslated;
}

export enum ActivityType {
  sportAndHealth = 'Sport & Health',
  other = 'Other',
}

export interface ISpecialty {
  id: string;
  title: string;
  translated?: ITranslated;
}

export interface IExperience {
  year: number;
  explanatory: string;
  hideOnSite: boolean;
}

export interface IAppointmentTypes {
  id: string;
  title: string;
}

export enum TargetAudience {
  Adults = 'adults',
  AllPublic = 'all public',
  Children = 'children',
  Groups = 'groups',
  WorkshopsAndConferences = 'workshops and conferences',
  Seniors = 'seniors',
  CompaniesAndPublicInstitutions = 'companies and public institutions',
}

export interface IRates {
  description: string;
  price: number;
  currency: string;
  active: boolean;
}

export interface RequestMessagePayload {
  professionalId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  message: string;
}
